import i18n from '@web/plugins/i18n'

/**
 * RFC 5322 совместимая валидация email адресов
 * Поддерживает полную спецификацию RFC 5322 для email адресов
 */

/**
 * Упрощенная RFC 5322 совместимая регулярка для email
 * Покрывает 99.99% реальных случаев использования
 */
const RFC_EMAIL_REGEX = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/

/**
 * Более строгая валидация для практического использования
 * Исключает некоторые теоретически валидные, но проблематичные варианты
 * Поддерживает + для создания алиасов (например, user+tag@gmail.com)
 */
const PRACTICAL_EMAIL_REGEX = /^[a-zA-Z0-9._+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/

export interface EmailValidationResult {
  isValid: boolean
  errors: string[]
  warnings: string[]
}

export interface EmailValidationOptions {
  /** Использовать строгую RFC 5322 валидацию */
  strict?: boolean
  /** Максимальная длина email (RFC 5321 ограничение: 320 символов) */
  maxLength?: number
  /** Максимальная длина локальной части (до @) */
  maxLocalLength?: number
  /** Максимальная длина доменной части (после @) */
  maxDomainLength?: number
  /** Разрешить интернационализированные доменные имена */
  allowInternational?: boolean
  /** Список запрещенных доменов */
  blockedDomains?: string[]
  /** Список разрешенных доменов (если указан, разрешены только эти домены) */
  allowedDomains?: string[]
}

const DEFAULT_OPTIONS: EmailValidationOptions = {
  strict: false,
  maxLength: 320,
  maxLocalLength: 64,
  maxDomainLength: 253,
  allowInternational: false,
  blockedDomains: [],
  allowedDomains: []
}

/**
 * Валидация email по RFC 5322 с дополнительными проверками
 */
export function validateEmail (
  email: string,
  options: EmailValidationOptions = {}
): EmailValidationResult {
  const opts = { ...DEFAULT_OPTIONS, ...options }
  const result: EmailValidationResult = {
    isValid: false,
    errors: [],
    warnings: []
  }

  // Базовые проверки
  if (!email) {
    result.errors.push(i18n.global.t('emailVerification.errors.empty'))
    return result
  }

  if (typeof email !== 'string') {
    result.errors.push(i18n.global.t('emailVerification.errors.invalidType'))
    return result
  }

  // Проверка длины
  if (email.length > opts.maxLength!) {
    result.errors.push(i18n.global.t('emailVerification.errors.invalidLength', { maxLength: opts.maxLength }))
    return result
  }

  // Базовая структурная проверка
  const atIndex = email.lastIndexOf('@')
  if (atIndex === -1) {
    result.errors.push(i18n.global.t('emailVerification.errors.invalidFormat@'))
    return result
  }

  if (atIndex === 0) {
    result.errors.push(i18n.global.t('emailVerification.errors.invalidStart'))
    return result
  }

  if (atIndex === email.length - 1) {
    result.errors.push(i18n.global.t('emailVerification.errors.invalidEnd'))
    return result
  }

  const localPart = email.substring(0, atIndex)
  const domainPart = email.substring(atIndex + 1)

  // Проверка локальной части
  if (localPart.length === 0) {
    result.errors.push(i18n.global.t('emailVerification.errors.invalidLocalPart'))
    return result
  }

  if (localPart.length > opts.maxLocalLength!) {
    result.errors.push(i18n.global.t('emailVerification.errors.invalidLocalPartLength', { maxLength: opts.maxLocalLength }))
    return result
  }

  // Проверка доменной части
  if (domainPart.length === 0) {
    result.errors.push(i18n.global.t('emailVerification.errors.invalidDomainPart'))
    return result
  }

  if (domainPart.length > opts.maxDomainLength!) {
    result.errors.push(i18n.global.t('emailVerification.errors.invalidDomainPartLength', { maxLength: opts.maxDomainLength }))
    return result
  }

  // Проверка на множественные @
  if (email.split('@').length > 2) {
    result.errors.push(i18n.global.t('emailVerification.errors.invalidMultipleAt'))
    return result
  }

  // Регулярка валидация
  const regex = opts.strict ? RFC_EMAIL_REGEX : PRACTICAL_EMAIL_REGEX
  if (!regex.test(email)) {
    result.errors.push(i18n.global.t('emailVerification.errors.invalidFormat'))
    return result
  }

  // Проверка локальной части на специальные случаи
  if (localPart.startsWith('.') || localPart.endsWith('.')) {
    result.errors.push(i18n.global.t('emailVerification.errors.invalidLocalPartStart'))
    return result
  }

  if (localPart.includes('..')) {
    result.errors.push(i18n.global.t('emailVerification.errors.invalidLocalPartDoubleDot'))
    return result
  }

  // Проверка доменной части
  if (domainPart.startsWith('.') || domainPart.endsWith('.')) {
    result.errors.push(i18n.global.t('emailVerification.errors.invalidDomainPartStart'))
    return result
  }

  if (domainPart.includes('..')) {
    result.errors.push(i18n.global.t('emailVerification.errors.invalidDomainPartDoubleDot'))
    return result
  }

  // Проверка доменных меток
  const domainLabels = domainPart.split('.')
  for (const label of domainLabels) {
    if (label.length === 0) {
      result.errors.push(i18n.global.t('emailVerification.errors.invalidDomainPartEmpty'))
      return result
    }

    if (label.length > 63) {
      result.errors.push(i18n.global.t('emailVerification.errors.invalidDomainPartLength', { maxLength: 63 }))
      return result
    }

    if (label.startsWith('-') || label.endsWith('-')) {
      result.errors.push(i18n.global.t('emailVerification.errors.invalidDomainPartDash'))
      return result
    }

    if (!opts.allowInternational && !/^[a-zA-Z0-9-]+$/.test(label)) {
      result.errors.push(i18n.global.t('emailVerification.errors.invalidDomainPartLatin'))
      return result
    }
  }

  // Проверка TLD (минимум 2 символа)
  const tld = domainLabels[domainLabels.length - 1]
  if (tld.length < 2) {
    result.errors.push(i18n.global.t('emailVerification.errors.invalidDomainPartMinLength', { minLength: 2 }))
    return result
  }

  if (!/^[a-zA-Z]+$/.test(tld)) {
    result.errors.push(i18n.global.t('emailVerification.errors.invalidDomainPartLetters'))
    return result
  }

  // Проверка заблокированных доменов
  if (opts.blockedDomains && opts.blockedDomains.length > 0) {
    const normalizedDomain = domainPart.toLowerCase()
    if (opts.blockedDomains.some(blocked => normalizedDomain === blocked.toLowerCase())) {
      result.errors.push(i18n.global.t('emailVerification.errors.blockedDomain'))
      return result
    }
  }

  // Проверка разрешенных доменов
  if (opts.allowedDomains && opts.allowedDomains.length > 0) {
    const normalizedDomain = domainPart.toLowerCase()
    if (!opts.allowedDomains.some(allowed => normalizedDomain === allowed.toLowerCase())) {
      result.errors.push(i18n.global.t('emailVerification.errors.unallowedDomain'))
      return result
    }
  }

  // Предупреждения
  if (localPart.length > 30) {
    result.warnings.push(i18n.global.t('emailVerification.errors.longLocalPart'))
  }

  if (domainLabels.length > 5) {
    result.warnings.push(i18n.global.t('emailVerification.errors.manyDomainLevels'))
  }

  // Если дошли до сюда - email валиден
  result.isValid = true
  return result
}

/**
 * Упрощенная функция для быстрой проверки валидности email
 */
export function isValidEmail (
  email: string,
  options?: EmailValidationOptions
): boolean {
  return validateEmail(email, options).isValid
}

/**
 * Нормализация email (приведение к нижнему регистру)
 */
export function normalizeEmail (email: string): string {
  if (!email) return email

  const atIndex = email.lastIndexOf('@')
  if (atIndex === -1) return email.toLowerCase()

  const localPart = email.substring(0, atIndex)
  const domainPart = email.substring(atIndex + 1).toLowerCase()

  return `${localPart}@${domainPart}`
}

/**
 * Получение домена из email
 */
export function getEmailDomain (email: string): string | null {
  const atIndex = email.lastIndexOf('@')
  if (atIndex === -1) return null

  return email.substring(atIndex + 1).toLowerCase()
}

/**
 * Получение локальной части email
 */
export function getEmailLocal (email: string): string | null {
  const atIndex = email.lastIndexOf('@')
  if (atIndex === -1) return null

  return email.substring(0, atIndex)
}

/**
 * Проверка на одноразовые email сервисы
 */
export function isDisposableEmail (email: string): boolean {
  const domain = getEmailDomain(email)
  if (!domain) return false

  // Список популярных одноразовых email сервисов
  const disposableDomains = [
    '10minutemail.com',
    'guerrillamail.com',
    'mailinator.com',
    'temp-mail.org',
    'yopmail.com',
    'throwaway.email',
    'getnada.com',
    'tempmail.net'
  ]

  return disposableDomains.includes(domain)
}

export default {
  validateEmail,
  isValidEmail,
  normalizeEmail,
  getEmailDomain,
  getEmailLocal,
  isDisposableEmail
}
